﻿import { BackgroundMedia } from '../background-media';
import getYoutubeId from 'get-youtube-id';
import urlQuerystring from 'url-querystring';
import YoutubePlayer from 'youtube-player';
import GlueFrame from 'glueframe';

export class Video {
  /**
   * Creates a new instance of the Video class for handling HTML5 Video, Youtube Videos and 23Videos
   * @param {HTMLElement} element Video DOM element
   */
  constructor(element) {
    element.Video = this;

    this._playing = false;
    this.element = element;
    this._props = {
      observer: null,
    };

    // check for background media element and initialize it if isn't already initalized
    const bgMediaElement = element.querySelector('.background-media');
    if (bgMediaElement) {
      this.BackgroundMedia = bgMediaElement.BackgroundMedia || new BackgroundMedia(bgMediaElement);
    }

    // determine video type
    this.isInternal = !!element.querySelector('.video__internal'); // if internal video from sitecore
    this.isYoutube = !!element.querySelector('.video__youtube'); // if youtube video
    this.isTwentyThree = !!element.querySelector('.video__twentythree'); // if twentythree video

    if ('IntersectionObserver' in window) {
      const onIntersection = (entries, observer) => {
          entries.forEach((x) => {
            if (x.isIntersecting) {
              this._onIntersection(x.target, (el) => {
                observer.unobserve(el);
              });
            }
          });
        },
        oIntersectionConfig = {
          rootMargin: '300px 0px',
        };

      this._props.observer = new IntersectionObserver(onIntersection, oIntersectionConfig);
      this._props.observer.observe(this.element);
    } else {
      this._onIntersection(this.element, null);
    }
  }

  /**
   * Handler for executing load-procedure for an item
   * @param {HTMLElement} el - Watched element
   * @param {Function} callback - Function to execute once load has completed
   */
  _onIntersection(el = HTMLElement, callback = Function) {
    // video initialization
    if (this.isTwentyThree) this.initializeTwentyThree();
    if (this.isInternal) this.initializeAsInternalVideo();
    if (this.isYoutube) this.initializeAsYoutubeVideo();
    if (typeof callback === 'function') {
      callback(el);
    }
  }

  /**
   * Is video paused?
   * @getter
   */
  get paused() {
    return !this._playing;
  }

  /**
   * Is video playing?
   * @getter
   */
  get playing() {
    return this._playing;
  }

  /**
   * Set current playing state and updated DOM eg.
   * @setter
   */
  set playing(state) {
    this._playing = state;
    if (this.isInternal) {
      state ? this.video.play() : this.video.pause();
    } else if (this.isYoutube) {
      state ? this.video.playVideo() : this.video.pauseVideo();
    } else if (this.isTwentyThree) {
      this.video.set('playing', state);
    }

    if (this.BackgroundMedia) {
      this.handleBackgroundMediaVisibility(state);
    }
    if (this.playButton) {
      state ? this.playButton.classList.add('hidden') : this.playButton.classList.remove('hidden');
    }
  }

  /**
   * Toggle playing state for a video (pause/play)
   */
  togglePlay() {
    this.playing ? this.pause() : this.play();
  }

  /**
   * Pause a video
   */
  pause() {
    //this.video.pauseVideo();
    this.playing = false;
  }

  /**
   * Start a video
   */
  play() {
    this.playing = true;
  }

  /**
   * Initialize twentythree video handling
   */
  initializeTwentyThree() {
    const oElement = this.element.querySelector('.video__twentythree__video');
    this.video = new GlueFrame(oElement);

    if (this.BackgroundMedia && this.BackgroundMedia.element) {
      this.BackgroundMedia.element.addEventListener('click', () => {
        // set source
        if (oElement) {
          oElement.src = oElement.dataset.src;
          oElement.addEventListener('load', () => {
            this.video = new GlueFrame(this.element.querySelector('.video__twentythree__video'));
            this.play();
          });
        }
      });
    }
  }

  /**
   * Initialize Internal Video handling
   */
  initializeAsInternalVideo() {
    this.video = this.element.querySelector('.video__internal__video');
    this.playButton = this.element.querySelector('.video__internal__play-button');
    if (this.video) {
      let videoSRC = this.video.querySelector('source')
        ? this.video.querySelector('source').getAttribute('src')
        : '';
      this.addInternalVideoEventListeners();
      let queryStringParameters = urlQuerystring(videoSRC).qs;

      this.video.controls = queryStringParameters.controls;
      this.video.loop = queryStringParameters.loop;

      if (queryStringParameters.muted === '1') {
        this.video.defaultMuted = true;
        this.video.muted = true;
        this.video.volume = 0;
      }

      if (queryStringParameters.autoplay === '1') {
        this.video.autoplay = true;
        this.video.play();
      }
    }
  }

  /**
   * Initialize YouTube video handling
   */
  initializeAsYoutubeVideo() {
    this.video = this.element.querySelector('.video__youtube__video');
    if (this.video.tagName !== 'IFRAME' && this.video.hasAttribute('data-src')) {
      const dataSourceURL = this.video.getAttribute('data-src') || '',
        queryStringParameters = urlQuerystring(dataSourceURL).qs;

      // load the youtube api and an initialize the video when it's ready
      /**
       * Youtube-nocookie throws console errors that blocks video events (play/pause/stop)
       * Youtube-nocookie is commented out to fix this issue
       * AND CookieBot have listed cookies from Youtube as mandatory
       * therefore we should be compliant with GDPR
       */
      this.video = YoutubePlayer(this.element.querySelector('.video__youtube__video'), {
        videoId: getYoutubeId(dataSourceURL),
        origin: window.location.origin,
        // host: '//www.youtube-nocookie.com',
        playerVars: Object.assign(
          {
            enablejsapi: 1,
            modestbranding: 1,
            rel: 0,
          },
          queryStringParameters,
        ),
      });

      this.addYoutubeVideoEventListeners(queryStringParameters);
    }
  }

  /**
   * Toggle background-media based on current video playing state
   * @param {Boolean} state - Is playing or not
   */
  handleBackgroundMediaVisibility(state) {
    const hiddenClass = 'is-hidden';

    if (this.BackgroundMedia.video) {
      this.BackgroundMedia.togglePlay(!state);
    }

    state
      ? this.BackgroundMedia.element.classList.add(hiddenClass)
      : this.BackgroundMedia.element.classList.remove(hiddenClass);
  }

  /**
   * Collection of events for handling internal videos
   */
  addInternalVideoEventListeners() {
    this.element.addEventListener('click', () => this.togglePlay());
    this.video.addEventListener('play', () => this.play());
    this.video.addEventListener('paused', () => this.pause());
    this.video.addEventListener('ended', () => this.pause());
  }

  /**
   * Collection of events for managing youtube videos
   * @param {Object} queryStringParameters - Object of paramteres from url
   */
  addYoutubeVideoEventListeners(queryStringParameters) {
    if (this.BackgroundMedia && this.BackgroundMedia.element) {
      this.BackgroundMedia.element.addEventListener('click', () => this.play());
    }

    this.video.on('ready', () => {
      this.element.querySelector('.video__youtube').style.pointerEvents = 'auto';

      if (queryStringParameters.muted === '1') {
        this.video.mute();
      }

      if (queryStringParameters.autoplay === '1') {
        this.play();
      }
    });

    // Event
    this.video.on('stateChange', (event) => {
      switch (event.data) {
        case window.YT.PlayerState.ENDED:
          this.pause();
          this.video.stopVideo();
          break;
      }
    });
  }
}
